// apps/web/src/components/threat-actors/threat-actor-utils.ts
//
// KAN-221 Threat Actors redesign — pure derivation helpers.
//
// Honesty contract (per the redesign brief): the list endpoint is capped at
// DEFAULT_LIMIT (50) server-side, so anything derived from the `actors` array
// here describes the LOADED VIEW, not the whole registry. The only corpus-wide
// figures come from the dedicated `/kpis` endpoint (ThreatActorKpis). Helpers
// are named/documented so callers can label the surface truthfully. No invented
// counts, risk scores, linked-CVE totals, or velocity metrics.

import type { ThreatActor, ThreatActorKpis } from '@/lib/types'
import { normaliseReferences } from '@/lib/references'

export type AttributionKey = 'state' | 'criminal' | 'hacktivist' | 'unknown'

export const ATTRIBUTION_ORDER: AttributionKey[] = [
  'state',
  'criminal',
  'hacktivist',
  'unknown',
]

const ATTRIBUTION_LABELS: Record<AttributionKey, string> = {
  state: 'State-sponsored',
  criminal: 'Criminal',
  hacktivist: 'Hacktivist',
  unknown: 'Unknown',
}

/**
 * Categorical attribution palette translated to existing IntelHub tokens
 * (Cyber Indigo + the semantic severity scale + neutral). These distinguish
 * the four classes in the mix bar / badges; they are NOT a severity ranking.
 */
const ATTRIBUTION_BADGE_CLASSES: Record<AttributionKey, string> = {
  state: 'bg-primary/10 text-primary border-primary/30',
  criminal: 'bg-severity-high/10 text-severity-high border-severity-high/30',
  hacktivist: 'bg-severity-info/10 text-severity-info border-severity-info/30',
  unknown: 'bg-muted text-muted-foreground border-border',
}

const ATTRIBUTION_BAR_CLASSES: Record<AttributionKey, string> = {
  state: 'bg-primary',
  criminal: 'bg-severity-high',
  hacktivist: 'bg-severity-info',
  unknown: 'bg-muted-foreground',
}

function asAttributionKey(value: string | null | undefined): AttributionKey {
  const normalised = value?.trim().toLowerCase()
  if (!normalised) return 'unknown'

  if (normalised === 'state' || normalised.includes('state-sponsored')) {
    return 'state'
  }
  if (normalised === 'criminal' || normalised.includes('cybercriminal')) {
    return 'criminal'
  }
  if (normalised === 'hacktivist' || normalised.includes('hacktivis')) {
    return 'hacktivist'
  }
  if (normalised === 'unknown' || normalised.includes('unknown')) {
    return 'unknown'
  }

  return 'unknown'
}

export function attributionLabel(value: string | null | undefined): string {
  return ATTRIBUTION_LABELS[asAttributionKey(value)]
}

export function attributionBadgeClasses(value: string | null | undefined): string {
  return ATTRIBUTION_BADGE_CLASSES[asAttributionKey(value)]
}

export function attributionBarClass(value: string | null | undefined): string {
  return ATTRIBUTION_BAR_CLASSES[asAttributionKey(value)]
}

// ─── Corpus-wide KPI derivations (ThreatActorKpis from /kpis) ──────────────────

export function kpiTotal(kpis: ThreatActorKpis): number {
  const c = kpis.attributionCounts
  return c.state + c.criminal + c.hacktivist + c.unknown
}

export function dominantShare(kpis: ThreatActorKpis): number {
  const total = kpiTotal(kpis)
  if (!kpis.dominantAttribution || total === 0) return 0
  return Math.round((kpis.attributionCounts[kpis.dominantAttribution] / total) * 100)
}

export interface AttributionMixSegment {
  key: AttributionKey
  label: string
  count: number
  pct: number
}

export function attributionMix(kpis: ThreatActorKpis): AttributionMixSegment[] {
  const total = kpiTotal(kpis)
  return ATTRIBUTION_ORDER.map((key) => {
    const count = kpis.attributionCounts[key]
    return {
      key,
      label: ATTRIBUTION_LABELS[key],
      count,
      pct: total > 0 ? (count / total) * 100 : 0,
    }
  })
}

// ─── Per-actor derivations ─────────────────────────────────────────────────────

export function referenceCount(actor: ThreatActor): number {
  return normaliseReferences(actor.references).length
}

export function externalIdCount(actor: ThreatActor): number {
  return (
    (actor.mitreId ? 1 : 0) +
    (actor.mispUuid ? 1 : 0) +
    (actor.malpediaId ? 1 : 0)
  )
}

export function hasExternalId(actor: ThreatActor): boolean {
  return externalIdCount(actor) > 0
}

// ─── Loaded-view aggregations (label these as "loaded" in the UI) ──────────────

export interface CountRow {
  label: string
  count: number
}

export function sourceCoverage(actors: ThreatActor[]): CountRow[] {
  return [
    { label: 'MITRE', count: actors.filter((a) => a.mitreId).length },
    { label: 'MISP', count: actors.filter((a) => a.mispUuid).length },
    { label: 'Malpedia', count: actors.filter((a) => a.malpediaId).length },
    { label: '≥1 ID', count: actors.filter((a) => hasExternalId(a)).length },
  ]
}

export function referenceRichCount(actors: ThreatActor[]): number {
  return actors.filter((a) => hasExternalId(a)).length
}
